import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, ExternalLink, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Resources() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", link: "", category: "General" });
  const [pendingFile, setPendingFile] = useState(null);

  const canCreate = ["Admin", "Manager"].includes(user.role);
  const canDelete = user.role === "Admin";

  const load = async () => {
    const r = await api.get("/resources");
    setItems(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      let payload = { ...form };
      if (pendingFile) {
        const fd = new FormData();
        fd.append("file", pendingFile);
        const fr = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        payload.file_id = fr.data.id;
      }
      await api.post("/resources", payload);
      toast.success("Resource added");
      setOpen(false);
      setForm({ title: "", description: "", link: "", category: "General" });
      setPendingFile(null);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    await api.delete(`/resources/${id}`);
    load();
  };

  // Group resources by category once per `items` change rather than re-filtering
  // inside the categories.map() loop on every render (O(C*N) → O(N)).
  const grouped = useMemo(() => {
    const m = new Map();
    for (const it of items) {
      const cat = it.category || "General";
      if (!m.has(cat)) m.set(cat, []);
      m.get(cat).push(it);
    }
    return m;
  }, [items]);
  const categories = useMemo(() => Array.from(grouped.keys()), [grouped]);

  return (
    <div className="min-h-screen bg-white" data-testid="resources-page">
      <PageHeader
        title="Resources"
        subtitle="Company Library"
        action={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" data-testid="btn-new-resource">
                <Plus size={14} className="mr-1.5" /> New Resource
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle style={{ fontFamily: "Outfit" }}>New Resource</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Title *</Label>
                  <Input className="mt-1 rounded-sm" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="input-res-title" />
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Textarea className="mt-1 rounded-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="input-res-desc" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["General", "Policies", "Templates", "Training", "Tax", "Legal", "Other"].map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">External Link (optional)</Label>
                    <Input className="mt-1 rounded-sm font-mono-data text-xs" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." data-testid="input-res-link" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Or upload file</Label>
                  <Input type="file" className="mt-1 rounded-sm" onChange={(e) => setPendingFile(e.target.files?.[0])} data-testid="input-res-file" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={create} data-testid="btn-save-resource">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="px-4 sm:px-8 py-6 space-y-8">
        {items.length === 0 && <div className="text-xs text-slate-400 text-center py-10 border border-dashed border-slate-200">No resources yet</div>}
        {categories.map((cat) => (
          <div key={cat}>
            <div className="gc-label mb-3">{cat}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(grouped.get(cat) || []).map((r) => (
                <div key={r.id} className="border border-slate-200 p-4 hover:border-[#0a2540]" data-testid={`resource-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{r.title}</div>
                      {r.description && <div className="mt-1 text-xs text-slate-500 line-clamp-2">{r.description}</div>}
                    </div>
                    {canDelete && (
                      <Button size="sm" variant="ghost" className="text-red-600 h-7 w-7 p-0" onClick={() => del(r.id)} data-testid={`btn-del-res-${r.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                    {r.link && (
                      <a href={r.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0a2540] hover:underline" data-testid={`res-link-${r.id}`}>
                        <ExternalLink size={12} /> Open link
                      </a>
                    )}
                    {r.file_id && (
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${r.file_id}/download`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#0a2540] hover:underline" data-testid={`res-file-${r.id}`}>
                        <FileText size={12} /> Download
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
